'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DocumentItem } from '@/components/pages/patients/LegalDocumentsPage/DocumentItem/DocumentItem';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { Loader } from '@/components/ui/Loader/Loader';
import { getPatientsLegalDocumentsList } from '@/api/patient/patient';
import { LegalDocument } from '@/api/patient/patient.types';
import s from '@/components/pages/patients/LegalDocumentsPage/LegalDocumentsPage.module.scss';

interface Props {
  currentPage: number;
}

const PER_PAGE = 10;

export const Documents = ({ currentPage }: Props) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [cards, setCards] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      if (searchParams.get('show_more') === 'true') return;

      setIsLoading(true);

      const documents = await getPatientsLegalDocumentsList({
        page: currentPage,
        per_page: PER_PAGE,
      });

      if (!documents) {
        setIsLoading(false);
        return;
      }

      setCards(documents.items);
      setTotalPages(documents.pages);
      setIsLoading(false);
    };

    fetchDoctors();
  }, [searchParams]);

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;
    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const documents = await getPatientsLegalDocumentsList({
      page: nextPage,
      per_page: PER_PAGE,
    });

    setCards((prev) => [...prev, ...documents.items]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  return (
    <>
      {isLoading && <Loader fixed />}
      <ul className={s.documents}>
        {cards.map((doc) => {
          return (
            <li key={doc.id}>
              <DocumentItem name={doc.document_title} file={doc.file} format={doc.file_format} />
            </li>
          );
        })}
      </ul>
      {cards.length > 0 && currentPage < totalPages && (
        <ShowMoreButton
          onClick={showMore}
          disabled={totalPages < 2}
          className={s.documents__showMore}
        />
      )}
      <Pagination totalPages={totalPages} />
    </>
  );
};